/**
 * The sample's docking units, one per stand of the sample's stand register.
 *
 * DEMONSTRATION records. Madinah has about twenty docking units, on the main
 * terminal's contact stands 101 to 121: a laser A-VDGS on each bridge head.
 * The remote stands, cargo, general aviation and maintenance are marshalled.
 * Makes, models, dates and dockings are the sample's, not the airport's.
 *
 * Planted so the screen has something to say:
 *  - 107 is faulted while SV1478 is planned onto it within the hour, so the
 *    ramp needs a marshaller there (and the delay screen is already moving
 *    SV1478 off 107 for another reason).
 *  - 104 is out for its six-monthly calibration.
 *  - 103's calibration has run past its interval.
 *  - 105 was never taught the A332, which UL225 is planned onto it with.
 *  - 102 aborted twice yesterday evening and is flagged degraded; 118's
 *    display has a dim row.
 *  - 114 comes due for calibration within the fortnight.
 *
 * Dockings are placed against the clock, like the movements, so the last
 * day's figures read the same whenever the demo is opened.
 */
import type { DockResult, DockingEvent, VdgsRegister, VdgsUnit } from "../domain/vdgs";

const HOUR = 3_600_000;
const DAY = 24 * HOUR;
const ago = (ms: number) => new Date(Date.now() - ms).toISOString();

const NARROW = ["A319", "A320", "A321", "A20N", "A21N", "B737", "B738", "B38M", "B39M"];
const WIDE = ["A332", "A333", "A339", "A359", "A35K", "B763", "B772", "B77W", "B788", "B789"];

/** a day of dockings: n of them, types drawn in turn, with the results given */
function day(n: number, types: string[], errors: number[], results: DockResult[] = []): DockingEvent[] {
  return Array.from({ length: n }, (_, i) => {
    const result = results[i] ?? "ok";
    const err = errors[i % errors.length] ?? 0;
    return {
      at: ago((i + 0.6) * (22 / n) * HOUR),
      aircraft: types[i % types.length] ?? "A320",
      stopErrorCm: result === "abort" ? 0 : err,
      seconds: result === "abort" ? 0 : 58 + ((i * 17) % 40),
      result: result === "ok" && Math.abs(err) > 50 ? (err > 0 ? ("overrun" as const) : ("short" as const)) : result,
      note:
        result === "abort"
          ? "No aircraft ID — pilot stopped short, marshaller brought it in"
          : result === "marshalled"
            ? "Unit out; marshalled"
            : undefined,
    };
  });
}

function unit(u: Partial<VdgsUnit> & Pick<VdgsUnit, "stand" | "kind" | "status">): VdgsUnit {
  return {
    tag: `VDGS-${u.stand}`,
    aircraftTypes: [],
    dockings: [],
    ...u,
  };
}

export function mockVdgs(): VdgsRegister {
  const safedock = { make: "ADB Safegate", model: "Safedock T2-24", bridgeInterlock: true };
  const units: VdgsUnit[] = [
    unit({
      stand: "101",
      kind: "a_vdgs",
      status: "in_service",
      ...safedock,
      installed: "2019-04-11",
      lastCalibration: ago(61 * DAY),
      lastEStopTest: ago(9 * DAY),
      aircraftTypes: NARROW,
      dockings: day(5, ["A320", "A320", "B738", "A321", "A320"], [8, -12, 20, 4, -6]),
    }),
    unit({
      stand: "102",
      kind: "a_vdgs",
      status: "degraded",
      ...safedock,
      installed: "2019-04-11",
      lastCalibration: ago(95 * DAY),
      lastEStopTest: ago(9 * DAY),
      fault: { since: ago(20 * HOUR), what: "Intermittent aircraft ID in low sun — two aborts" },
      aircraftTypes: NARROW,
      dockings: day(6, ["A320", "B738", "A320"], [15, 30, -10, 0, 35, 22], ["ok", "abort", "ok", "abort", "ok", "ok"]),
    }),
    unit({
      stand: "103",
      kind: "a_vdgs",
      status: "in_service",
      ...safedock,
      installed: "2019-04-11",
      lastCalibration: ago(197 * DAY),
      lastEStopTest: ago(40 * DAY),
      aircraftTypes: NARROW,
      dockings: day(5, ["B738", "A320", "A321"], [42, 55, 38, 61, 30]),
      notes: "Stop errors creeping past the mark since the spring — calibration is overdue.",
    }),
    unit({
      stand: "104",
      kind: "a_vdgs",
      status: "maintenance",
      ...safedock,
      installed: "2019-04-11",
      lastCalibration: ago(182 * DAY),
      lastEStopTest: ago(9 * DAY),
      fault: { since: ago(3 * HOUR), what: "Six-monthly calibration in progress" },
      aircraftTypes: NARROW,
      dockings: day(3, ["A320", "B738"], [5, -8, 12]),
    }),
    unit({
      stand: "105",
      kind: "a_vdgs",
      status: "in_service",
      ...safedock,
      installed: "2021-09-02",
      lastCalibration: ago(33 * DAY),
      lastEStopTest: ago(9 * DAY),
      /* taught the narrowbodies and the B763, never the A332 it now takes */
      aircraftTypes: [...NARROW, "B763"],
      dockings: day(4, ["A321", "A320", "B763"], [6, -4, 10, 2]),
    }),
    unit({
      stand: "106",
      kind: "a_vdgs",
      status: "in_service",
      make: "FMT",
      model: "APIS++",
      bridgeInterlock: true,
      installed: "2020-02-18",
      lastCalibration: ago(170 * DAY),
      lastEStopTest: ago(12 * DAY),
      aircraftTypes: [...NARROW, ...WIDE],
      dockings: day(3, ["B77W", "A332", "A320"], [10, -14, 6]),
    }),
    unit({
      stand: "107",
      kind: "a_vdgs",
      status: "fault",
      make: "FMT",
      model: "APIS++",
      bridgeInterlock: true,
      installed: "2020-02-18",
      lastCalibration: ago(88 * DAY),
      lastEStopTest: ago(12 * DAY),
      fault: { since: ago(2 * HOUR), what: "Laser scanner not acquiring — display dark" },
      aircraftTypes: [...NARROW, ...WIDE],
      dockings: [
        ...day(2, ["A320", "B77W"], [4, 12]).map((d) => ({
          ...d,
          at: new Date(Date.parse(d.at) - 4 * HOUR).toISOString(),
        })),
        {
          at: ago(70 * 60_000),
          aircraft: "A320",
          stopErrorCm: 0,
          seconds: 0,
          result: "marshalled" as const,
          note: "Unit out; marshalled",
        },
      ],
    }),
    unit({ stand: "R1", kind: "marshaller", status: "not_fitted", tag: "STAND-R1" }),
    /* 108–121: the rest of the main terminal */
    ...Array.from({ length: 14 }, (_, i) => {
      const stand = String(108 + i);
      const wide = i >= 8;
      return unit({
        stand,
        kind: "a_vdgs",
        status: stand === "118" ? "degraded" : "in_service",
        ...(wide ? { make: "FMT", model: "APIS++", bridgeInterlock: true } : safedock),
        installed: wide ? "2020-02-18" : "2019-04-11",
        lastCalibration: ago((stand === "114" ? 172 : 30 + ((i * 23) % 130)) * DAY),
        lastEStopTest: ago((8 + i) * DAY),
        fault:
          stand === "118"
            ? { since: ago(30 * HOUR), what: "Display row 3 dim — readable, LED board ordered" }
            : undefined,
        aircraftTypes: wide ? [...NARROW, "A332", "A333", "B763", "B788"] : NARROW,
        dockings: day(3 + (i % 3), wide ? ["A320", "A332", "B763"] : ["A320", "B738", "A321", "A20N"], [
          6 + i,
          -9,
          14,
          -3 - i,
          11,
        ]),
      });
    }),
    unit({ stand: "R2", kind: "marshaller", status: "not_fitted", tag: "STAND-R2" }),
    unit({ stand: "R3", kind: "marshaller", status: "not_fitted", tag: "STAND-R3" }),
    unit({ stand: "R4", kind: "marshaller", status: "not_fitted", tag: "STAND-R4" }),
    unit({ stand: "85", kind: "marshaller", status: "not_fitted", tag: "STAND-85" }),
    unit({ stand: "F1", kind: "marshaller", status: "not_fitted", tag: "STAND-F1" }),
    unit({ stand: "G1", kind: "marshaller", status: "not_fitted", tag: "STAND-G1" }),
    unit({ stand: "M1", kind: "none", status: "not_fitted", tag: "STAND-M1" }),
  ];
  return { units };
}
